/**
 * PATH: packages/email-hub/src/feature/reader-header/model/placements.ts
 * FILE_ID: EMAILHUB-READERHEADER-MODEL-PLACEMENTS-001
 * PURPOSE: Normalize plural placements and select one deterministic primary.
 * ROLE IN ARCHITECTURE: Pure domain selector between API payloads and reader UI.
 * MAIN EXPORTS: selectReaderPlacements, selectPrimaryPlacement, placementIsUserTouched.
 * NON-RESPONSIBILITIES: This file does not fetch or mutate Kanban allocations.
 * NOTES FOR FUTURE AI: Preserve plural data; never collapse the API response before the manager receives it.
 */
import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'

import type { Email } from '../../hooks/useEmails'

function placementKey(summary: EmailAllocationSummary): string {
  return summary.allocation_id || `${summary.board_id}:${summary.lane_id}:${summary.card_id}`
}

export function placementNeedsReview(summary: EmailAllocationSummary): boolean {
  // Pending is the privacy default for every auto-filing. It is not a
  // request for a person. Only a parked review (needs_review) asks
  // the operator to confirm the filing.
  return summary.needs_review === true
}

/**
 * Convert old singular and new plural payloads into one deduplicated list.
 *
 * Inputs: an email carrying optional `allocation_summaries` and legacy
 * `allocation_summary`. Output: all unique placements, preserving server order.
 * Side effects: none. Failure mode: malformed/absent arrays safely return the
 * valid singular fallback or an empty list.
 */
export function selectReaderPlacements(
  email: Pick<Email, 'allocation_summary' | 'allocation_summaries'>,
): EmailAllocationSummary[] {
  const candidates = Array.isArray(email.allocation_summaries)
    ? [...email.allocation_summaries]
    : []
  if (email.allocation_summary) candidates.push(email.allocation_summary)

  const seen = new Set<string>()
  return candidates.filter((summary) => {
    if (!summary?.card_id || !summary?.board_id || !summary?.lane_id) return false
    const key = placementKey(summary)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Sources that mean an operator explicitly touched the placement. */
const USER_TOUCHED_SOURCES: ReadonlySet<EmailAllocationSummary['source']> =
  new Set(['USER_MANUAL', 'AI_SUGGESTED_USER_CONFIRMED'])

export function placementIsUserTouched(
  summary: EmailAllocationSummary,
): boolean {
  return USER_TOUCHED_SOURCES.has(summary.source)
}

/**
 * Pick the at-rest placement without hiding siblings (plan Filed To lane:
 * review-required first, then the most recently user-touched active
 * placement).
 *
 * The backend emits placements most-recent-first, so "most recently
 * user-touched" is the FIRST user-touched entry in server order; when the
 * operator never touched any placement, the most recent overall wins. The
 * full list remains available to the Placement Manager.
 *
 * `overrideId` is the operator's manual "designate primary" choice
 * (placements/primaryOverride.ts). Review-required placements STILL win over
 * it — a pending decision must never be hidden behind a cosmetic preference.
 * A stale override (its placement was removed) is ignored.
 */
export function selectPrimaryPlacement(
  placements: readonly EmailAllocationSummary[],
  overrideId?: string | null,
): EmailAllocationSummary | null {
  const overridden = overrideId
    ? placements.find((placement) => placement.allocation_id === overrideId)
    : undefined
  return (
    placements.find(placementNeedsReview) ??
    overridden ??
    placements.find(placementIsUserTouched) ??
    placements[0] ??
    null
  )
}
